import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, Download, Gavel } from 'lucide-react';
import { adminAssessment, adminInviteCodes } from '@/api/admin';
import type {
  AssessmentDimensionResult,
  AssessmentRecord,
  AssessmentRecordStatus,
} from '@/api/extras';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pill } from '@/components/widgets/Pill';
import { toast } from '@/components/ui/sonner';
import { ApiError } from '@/lib/http';
import { fmtDateTime, fmtMoney, moneyToNumber } from '@/lib/format';
import { downloadCsv } from '@/lib/csv';
import { cn } from '@/lib/utils';

const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;

const STATUS_FILTERS: { label: string; value: '' | AssessmentRecordStatus }[] = [
  { label: '全部', value: '' },
  { label: '达标', value: 'passed' },
  { label: '未达标', value: 'failed' },
  { label: '已改判', value: 'overridden' },
];

/** 本地日期 → YYYY-MM-DD(按本机时区,与后端交易日界近似;跨日边界由后端权威判定)。 */
function ymd(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 快捷范围:今日 / 近 7 日 / 本月。 */
function quickRange(kind: 'today' | 'last7' | 'month'): { from: string; to: string } {
  const now = new Date();
  const to = ymd(now);
  if (kind === 'today') return { from: to, to };
  if (kind === 'last7') {
    const f = new Date(now);
    f.setDate(f.getDate() - 6);
    return { from: ymd(f), to };
  }
  return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to };
}

function channelLabel(inviteCode: string | null, channelAlias: string | null): string {
  return channelAlias || inviteCode || '—';
}

export function AssessmentRecords() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [userId, setUserId] = useState('');
  const [phone, setPhone] = useState('');
  const [inviteCodeId, setInviteCodeId] = useState('');
  const [status, setStatus] = useState<'' | AssessmentRecordStatus>('');
  const [pageSize, setPageSize] = useState<number>(50);
  const [page, setPage] = useState(1);
  const [override, setOverride] = useState<AssessmentRecord | null>(null);

  // 任一筛选变更回到第 1 页。
  const setFromP = (v: string) => { setFrom(v); setPage(1); };
  const setToP = (v: string) => { setTo(v); setPage(1); };
  const setUserIdP = (v: string) => { setUserId(v); setPage(1); };
  const setPhoneP = (v: string) => { setPhone(v); setPage(1); };
  const setInviteCodeIdP = (v: string) => { setInviteCodeId(v); setPage(1); };
  const setStatusP = (v: '' | AssessmentRecordStatus) => { setStatus(v); setPage(1); };
  const setPageSizeP = (v: number) => { setPageSize(v); setPage(1); };
  const applyQuick = (kind: 'today' | 'last7' | 'month') => {
    const r = quickRange(kind);
    setFrom(r.from);
    setTo(r.to);
    setPage(1);
  };

  const [exporting, setExporting] = useState(false);

  // 渠道下拉:拉全部邀请码(突破单页 200 上限,避免超 200 个渠道后无法筛选后续渠道)。
  const channelsQ = useQuery({
    queryKey: ['admin', 'invite-codes', 'all-channels'],
    queryFn: () => adminInviteCodes.listAll(),
  });
  const channels = channelsQ.data ?? [];

  // 当前筛选参数(不含分页):listQ 与 CSV 导出共用,保证导出口径与页面筛选一致。
  const filterParams = () => ({
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(userId.trim() ? { userId: userId.trim() } : {}),
    ...(phone.trim() ? { phone: phone.trim() } : {}),
    ...(inviteCodeId ? { inviteCodeId } : {}),
    ...(status ? { status } : {}),
  });

  const listQ = useQuery({
    queryKey: ['admin', 'assessment-records', from, to, userId, phone, inviteCodeId, status, pageSize, page],
    queryFn: () => adminAssessment.records({ page, pageSize, ...filterParams() }),
    placeholderData: (prev) => prev,
  });

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const summary = listQ.data?.summary;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const passRate = summary && summary.total > 0 ? (summary.passedCount / summary.total) * 100 : 0;

  // CSV 导出:按**当前筛选**循环翻页拉全量(非仅当前页),且独立请求——不复用可能因 placeholderData
  // 滞留上一筛选的 listQ.data,彻底规避「只导当前页」与「切筛选瞬间导出旧数据」两个问题。
  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const base = filterParams();
      const size = 200; // 后端 records 单页上限
      const all: AssessmentRecord[] = [];
      // 500 页硬上限仅作循环安全兜底(=10 万条,远超真实规模)。
      for (let p = 1; p <= 500; p++) {
        const res = await adminAssessment.records({ page: p, pageSize: size, ...base });
        const rows = res.items ?? [];
        all.push(...rows);
        if (rows.length < size || all.length >= (res.total ?? all.length)) break;
      }
      if (all.length === 0) {
        toast.error('没有可导出的记录');
        return;
      }
      downloadCsv('assessment-records', all, [
        { key: 'userId', header: '用户ID' },
        { key: 'nickname', header: '昵称', format: (_, r) => r.nickname ?? '' },
        { key: 'phone', header: '手机号', format: (_, r) => r.phone ?? '' },
        { key: 'channel', header: '渠道', format: (_, r) => (r.channelAlias || r.inviteCode || '') },
        { key: 'tradeDate', header: '交易日' },
        { key: 'tradeCount', header: '交易笔数' },
        { key: 'realizedPnl', header: '当日盈亏', format: (_, r) => moneyToNumber(r.realizedPnl).toFixed(2) },
        { key: 'tradingCost', header: '交易成本', format: (_, r) => moneyToNumber(r.tradingCost).toFixed(2) },
        { key: 'computedPassed', header: '系统判定', format: (_, r) => (r.computedPassed ? '达标' : '未达标') },
        { key: 'effectivePassed', header: '最终结果', format: (_, r) => (r.effectivePassed ? '达标' : '未达标') },
        { key: 'overridden', header: '已改判', format: (_, r) => (r.overridden ? '是' : '') },
      ]);
    } catch (e) {
      toast.error('导出失败', { description: e instanceof ApiError ? e.message : '未知错误' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-brand" />
            考核记录
          </h1>
          <p className="text-sm text-ink-2 mt-1">
            逐用户每交易日考核结果 · 维度达标明细 · 可手动改判 / 撤销改判(需填原因)
          </p>
        </div>
        <Button variant="outline" size="sm" disabled={exporting || (summary?.total ?? 0) === 0} onClick={exportCsv}>
          <Download className="h-4 w-4" />
          {exporting ? '导出中...' : '导出 CSV'}
        </Button>
      </header>

      {/* 统计卡(数据取响应 summary,按当前筛选全量聚合) */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="记录数" value={summary ? String(summary.total) : '—'} sub={`已改判 ${summary?.overriddenCount ?? 0}`} />
        <StatCard label="达标" value={summary ? String(summary.passedCount) : '—'} tone="up" />
        <StatCard label="未达标" value={summary ? String(summary.failedCount) : '—'} tone="down" />
        <StatCard label="达标率" value={summary && summary.total > 0 ? `${passRate.toFixed(1)}%` : '—'} tone="brand" />
      </section>

      {/* 筛选区 */}
      <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
        <div className="space-y-1">
          <Label className="text-xs text-ink-2">交易日 从</Label>
          <Input type="date" className="w-40 num" value={from} onChange={(e) => setFromP(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-ink-2">至</Label>
          <Input type="date" className="w-40 num" value={to} onChange={(e) => setToP(e.target.value)} />
        </div>
        <div className="flex items-center gap-1.5 pb-0.5">
          <Button variant="outline" size="sm" onClick={() => applyQuick('today')}>今日</Button>
          <Button variant="outline" size="sm" onClick={() => applyQuick('last7')}>近 7 日</Button>
          <Button variant="outline" size="sm" onClick={() => applyQuick('month')}>本月</Button>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-ink-2">渠道</Label>
          <select
            value={inviteCodeId}
            onChange={(e) => setInviteCodeIdP(e.target.value)}
            className="h-9 w-40 rounded-md border border-line bg-bg-3 px-2 text-sm text-ink-1"
          >
            <option value="">全部渠道</option>
            {channels.map((c) => (
              <option key={c.id} value={c.id}>{c.alias || c.code}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-ink-2">手机号</Label>
          <Input className="w-40 num" placeholder="模糊匹配" value={phone} onChange={(e) => setPhoneP(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-ink-2">用户 ID</Label>
          <Input className="w-32 num" placeholder="全部用户" value={userId} onChange={(e) => setUserIdP(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-ink-2">状态</Label>
          <select
            value={status}
            onChange={(e) => setStatusP(e.target.value as '' | AssessmentRecordStatus)}
            className="h-9 rounded-md border border-line bg-bg-3 px-2 text-sm text-ink-1"
          >
            {STATUS_FILTERS.map((f) => (
              <option key={f.value || 'all'} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-ink-2">每页</Label>
          <select
            value={pageSize}
            onChange={(e) => setPageSizeP(Number(e.target.value))}
            className="h-9 rounded-md border border-line bg-bg-3 px-2 text-sm text-ink-1 num"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            考核记录 · {listQ.data ? total : '—'} 条
            {total > 0 && (
              <span className="ml-2 text-xs text-ink-3 font-normal num">第 {page}/{totalPages} 页</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {listQ.isError && (
            <div className="px-4 py-3 text-sm text-down-dim">
              无法加载:{listQ.error instanceof ApiError ? listQ.error.message : '未知错误'}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead className="bg-bg-3 text-ink-2 text-xs">
                <tr>
                  <th className="px-3 py-2 text-left font-normal">用户</th>
                  <th className="px-3 py-2 text-left font-normal">渠道</th>
                  <th className="px-3 py-2 text-left font-normal">交易日</th>
                  <th className="px-3 py-2 text-right font-normal">交易笔数</th>
                  <th className="px-3 py-2 text-right font-normal">当日盈亏</th>
                  <th className="px-3 py-2 text-right font-normal">交易成本</th>
                  <th className="px-3 py-2 text-left font-normal">维度达标</th>
                  <th className="px-3 py-2 text-left font-normal">最终结果</th>
                  <th className="px-3 py-2 text-right font-normal">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <RecordRow key={`${r.userId}-${r.tradeDate}`} r={r} onOverride={setOverride} />
                ))}
                {items.length === 0 && !listQ.isLoading && (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-ink-3">没有符合条件的考核记录</td>
                  </tr>
                )}
                {listQ.isLoading && (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-ink-3">加载中...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {total > pageSize && (
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-line">
              <Button variant="outline" size="sm" disabled={page <= 1 || listQ.isFetching} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                上一页
              </Button>
              <span className="text-xs text-ink-3 num">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages || listQ.isFetching} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                下一页
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <OverrideDialog record={override} onClose={() => setOverride(null)} />
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  tone = 'ink',
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'ink' | 'up' | 'down' | 'brand';
}) {
  const valueClass =
    tone === 'up' ? 'text-up' : tone === 'down' ? 'text-down' : tone === 'brand' ? 'text-brand' : 'text-ink-0';
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-ink-2">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={cn('text-2xl font-bold num', valueClass)}>{value}</div>
        {sub && <p className="text-xs text-ink-3 mt-1 num">{sub}</p>}
      </CardContent>
    </Card>
  );
}

const DIM_LABEL: Record<AssessmentDimensionResult['type'], string> = {
  tradeCount: '笔数',
  profit: '盈利',
  loss: '亏损',
};

/** 单维度小标签:达标绿 / 未达标红;笔数显示 当前/目标,金额显示 ✓/✗。 */
function DimensionPill({ d }: { d: AssessmentDimensionResult }) {
  const label = DIM_LABEL[d.type];
  let text: string;
  if (d.type === 'tradeCount') {
    text = `${label} ${d.currentCount ?? 0}/${d.targetCount ?? 0}`;
  } else {
    text = `${label} ${d.met ? '✓' : '✗'}`;
  }
  let title = '';
  if (d.type === 'tradeCount') {
    title = `当日开仓 ${d.currentCount ?? 0} 笔 / 目标 ${d.targetCount ?? 0} 笔`;
  } else if (d.type === 'profit') {
    title = `当日盈利 ${fmtMoney(d.currentValue)} / 目标 ≥ ${fmtMoney(d.targetValue)}`;
  } else {
    title = `当日亏损 ${fmtMoney(d.currentValue)} / 上限 ≤ ${fmtMoney(d.targetValue)}`;
  }
  return (
    <span title={title}>
      <Pill tone={d.met ? 'up' : 'down'}>{text}</Pill>
    </span>
  );
}

function RecordRow({ r, onOverride }: { r: AssessmentRecord; onOverride: (r: AssessmentRecord) => void }) {
  const pnl = moneyToNumber(r.realizedPnl);
  const overrideTitle = r.overridden
    ? `改判为「${r.effectivePassed ? '达标' : '未达标'}」` +
      (r.overrideReason ? ` · 原因:${r.overrideReason}` : '') +
      (r.overrideAdmin ? ` · 操作人:${r.overrideAdmin}` : '') +
      (r.overrideAt ? ` · ${fmtDateTime(r.overrideAt)}` : '')
    : undefined;
  return (
    <tr className="border-t border-line row-hover">
      <td className="px-3 py-2" title={`用户ID ${r.userId}`}>
        <div className="text-ink-1">{r.nickname || r.phone || `用户${r.userId}`}</div>
        <div className="text-xs text-ink-3 num">{r.phone || `ID ${r.userId}`}</div>
      </td>
      <td className="px-3 py-2 text-ink-2" title={r.inviteCode ? `邀请码 ${r.inviteCode}` : undefined}>
        {channelLabel(r.inviteCode, r.channelAlias)}
      </td>
      <td className="px-3 py-2 num text-ink-2">{r.tradeDate}</td>
      <td className="px-3 py-2 num text-right text-ink-1">{r.tradeCount}</td>
      <td className={cn('px-3 py-2 num text-right', pnl > 0 ? 'text-up' : pnl < 0 ? 'text-down' : 'text-ink-1')}>
        {fmtMoney(r.realizedPnl, { sign: true })}
      </td>
      <td className="px-3 py-2 num text-right text-ink-1">{fmtMoney(r.tradingCost)}</td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-1">
          {r.dimensions.length === 0 ? (
            <span className="text-ink-3 text-xs">未启用维度</span>
          ) : (
            r.dimensions.map((d) => <DimensionPill key={d.type} d={d} />)
          )}
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-1">
          <Pill tone={r.effectivePassed ? 'up' : 'down'}>{r.effectivePassed ? '达标' : '未达标'}</Pill>
          {r.overridden && (
            <span title={overrideTitle}>
              <Pill tone="warn">已改判</Pill>
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-right">
        <Button variant="ghost" size="sm" onClick={() => onOverride(r)}>
          <Gavel className="h-3.5 w-3.5" />
          手动改判
        </Button>
      </td>
    </tr>
  );
}

/**
 * 手动改判弹窗:置为达标/未达标(默认达标)+ reason(≥4字);提交 POST override。
 * 若该记录 overridden=true,额外提供「撤销改判」(DELETE,同样要 reason)。
 */
function OverrideDialog({ record, onClose }: { record: AssessmentRecord | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [passed, setPassed] = useState(true);
  const [reason, setReason] = useState('');

  // 打开新记录时复位表单(默认达标)。
  useEffect(() => {
    if (record) {
      setPassed(true);
      setReason('');
    }
  }, [record]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'assessment-records'] });

  const override = useMutation({
    mutationFn: () => {
      if (!record) throw new Error('no record');
      return adminAssessment.override(record.userId, record.tradeDate, {
        passed,
        reason: reason.trim(),
      });
    },
    onSuccess: () => {
      toast.success('已改判', { description: `已置为「${passed ? '达标' : '未达标'}」` });
      invalidate();
      onClose();
    },
    onError: (e) => toast.error('改判失败', { description: e instanceof ApiError ? e.message : '' }),
  });

  const revoke = useMutation({
    mutationFn: () => {
      if (!record) throw new Error('no record');
      return adminAssessment.revokeOverride(record.userId, record.tradeDate, {
        reason: reason.trim(),
      });
    },
    onSuccess: () => {
      toast.success('已撤销改判', { description: '恢复系统判定' });
      invalidate();
      onClose();
    },
    onError: (e) => toast.error('撤销失败', { description: e instanceof ApiError ? e.message : '' }),
  });

  const busy = override.isPending || revoke.isPending;
  const reasonOk = reason.trim().length >= 4;
  const canSubmit = reasonOk && !busy;

  return (
    <Dialog open={!!record} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md">
        {record && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Gavel className="h-5 w-5 text-brand" />
                手动改判
              </DialogTitle>
              <DialogDescription>
                用户ID {record.userId}{record.phone ? ` · ${record.phone}` : ''} · 交易日 {record.tradeDate} · 系统判定「{record.computedPassed ? '达标' : '未达标'}」。
                改判即时生效,需填原因(≥4 字)。
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>改判结果</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant={passed ? 'default' : 'outline'}
                    size="sm"
                    disabled={busy}
                    onClick={() => setPassed(true)}
                  >
                    置为达标
                  </Button>
                  <Button
                    type="button"
                    variant={!passed ? 'destructive' : 'outline'}
                    size="sm"
                    disabled={busy}
                    onClick={() => setPassed(false)}
                  >
                    置为未达标
                  </Button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ovr-reason">操作原因(至少 4 字)</Label>
                <Input
                  id="ovr-reason"
                  placeholder="例如:当日系统异常导致漏记开仓"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={busy}
                />
              </div>
            </div>

            <DialogFooter className="flex-wrap gap-2">
              {record.overridden && (
                <Button
                  variant="outline"
                  className="mr-auto"
                  disabled={!reasonOk || busy}
                  title="撤销改判,恢复系统判定(同样需填原因)"
                  onClick={() => revoke.mutate()}
                >
                  {revoke.isPending ? '撤销中...' : '撤销改判'}
                </Button>
              )}
              <Button variant="outline" onClick={onClose} disabled={busy}>
                取消
              </Button>
              <Button onClick={() => override.mutate()} disabled={!canSubmit}>
                {override.isPending ? '提交中...' : '确认改判'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
